import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, MOCK_FORCED, definirEmpresaEmFoco, request } from './api';
import { useSession } from './auth';
import { EMPRESAS, EMPRESA_DEMO_ID, empresaPorId } from './mock/catalogo';
import type { CompanySummary } from '../types';

/**
 * Empresa em foco no painel (RNF12).
 *
 * Duas situacoes, um so hook:
 *
 *  - LOJISTA: a empresa e a da sessao, e nao ha troca. `companies` tem um item.
 *  - EQUIPE DO CONVERTE FACIL (`platform_admin`): pode abrir o CRM de qualquer
 *    empresa cadastrada. A escolha fica no navegador (localStorage) e o
 *    cabecalho mostra logo e nome da empresa aberta, para nunca haver duvida
 *    de qual painel esta na tela.
 *
 * Na previa (VITE_USE_MOCK_DATA=true) a lista vem do catalogo ficticio e as
 * alteracoes ficam no navegador. Em modo real (25/09/2026):
 *
 *  1. A lista da equipe vem de `GET /api/admin/companies` (campo `companies`).
 *     Ate ela chegar, a escolha guardada NAO e descartada: a tela usa a
 *     empresa da sessao e troca quando a lista carrega.
 *  2. A empresa escolhida viaja em `X-Company-Id` (lib/api.ts,
 *     `definirEmpresaEmFoco`) e o SERVIDOR decide se aceita (papel + empresa
 *     ativa). O foco e ligado DURANTE o render do provedor, antes dos filhos:
 *     efeitos de filho rodam antes dos do pai no React, e a primeira busca da
 *     pagina sairia sem o cabecalho, com os numeros da empresa errada.
 *  3. `updateCompany` e otimista: aplica na hora, chama
 *     `PATCH /api/admin/companies/:id` e, se falhar, DESFAZ e mostra o aviso.
 *     Devolve `true` so quando o servidor confirmou -- quem mostra "Salvo"
 *     espera a promessa. O status da tela (ativa/onboarding/pausada) vira o do
 *     banco (active/suspended) so aqui, na fronteira.
 */

/** Campos que a administracao pode alterar no cadastro (comissao e cobranca). */
export type CompanyPatch = Partial<Pick<CompanySummary, 'feeBps' | 'billingModel' | 'refundPolicy' | 'name' | 'segment' | 'logoTone' | 'status'>>;

interface CompanyContextValue {
  readonly company: CompanySummary;
  readonly companies: readonly CompanySummary[];
  readonly isPlatformAdmin: boolean;
  readonly selectCompany: (id: string) => void;
  /**
   * Altera o cadastro de uma empresa (so `platform_admin`). Na previa, a
   * alteracao fica no navegador (`cf.empresas.ajustes`) e vale em TODAS as
   * telas -- e assim que a comissao mudada na administracao aparece na aba da
   * propria empresa (pedido do dono, 25/09/2026). Em modo real e
   * `PATCH /api/admin/companies/:id`, com desfazer e aviso se falhar.
   * Resolve `true` quando a alteracao valeu.
   */
  readonly updateCompany: (id: string, patch: CompanyPatch) => Promise<boolean>;
  /** Recarrega a lista da equipe (modo real). No-op para lojista e na previa. */
  readonly reloadCompanies: () => void;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);
const STORAGE_KEY = 'cf.empresa';
const AJUSTES_KEY = 'cf.empresas.ajustes';

type Ajustes = Readonly<Record<string, CompanyPatch>>;

export function lerAjustesDeEmpresas(): Ajustes {
  try {
    const raw = window.localStorage.getItem(AJUSTES_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Ajustes) : {};
  } catch {
    return {};
  }
}

function guardarAjustes(ajustes: Ajustes): void {
  try {
    window.localStorage.setItem(AJUSTES_KEY, JSON.stringify(ajustes));
  } catch {
    // Armazenamento indisponivel nao derruba o painel.
  }
}

function aplicarAjuste(empresa: CompanySummary, ajustes: Ajustes): CompanySummary {
  const patch = ajustes[empresa.id];
  return patch === undefined ? empresa : { ...empresa, ...patch };
}

function lerEscolha(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function guardarEscolha(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Armazenamento indisponivel nao derruba o painel.
  }
}

/** Corpo do PATCH no vocabulario do servidor (so os campos que ele aceita). */
function corpoDoPatch(patch: CompanyPatch): Record<string, unknown> {
  const corpo: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    corpo['name'] = patch.name;
  }
  if (patch.segment !== undefined) {
    corpo['segment'] = patch.segment;
  }
  if (patch.feeBps !== undefined) {
    corpo['feeBps'] = patch.feeBps;
  }
  if (patch.billingModel !== undefined) {
    corpo['billingModel'] = patch.billingModel;
  }
  if (patch.refundPolicy !== undefined) {
    corpo['refundPolicy'] = patch.refundPolicy;
  }
  if (patch.logoTone !== undefined) {
    corpo['logoTone'] = patch.logoTone;
  }
  if (patch.status !== undefined) {
    // `onboarding` nao e escolha: e ativa ainda sem webhook (derivado no servidor).
    corpo['status'] = patch.status === 'pausada' ? 'suspended' : 'active';
  }
  return corpo;
}

function mensagemDeFalha(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'nao_pode_suspender_a_propria_empresa') {
      return 'A empresa do próprio Converte Fácil não pode ser pausada: a equipe perderia o acesso ao painel. Nada foi salvo.';
    }
    if (error.status === 400) {
      return 'O servidor recusou a alteração (valor fora da faixa aceita). Nada foi salvo.';
    }
    return `${error.userMessage} Nada foi salvo.`;
  }
  return 'Falha inesperada ao salvar. Nada foi salvo.';
}

interface ListaDaEquipe {
  readonly companies: readonly CompanySummary[];
}

export function CompanyProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const session = useSession();
  const isPlatformAdmin = session.user.role === 'platform_admin';
  const listaRemota = isPlatformAdmin && !MOCK_FORCED;

  const daSessao = useMemo<CompanySummary>(() => {
    const catalogada = MOCK_FORCED ? empresaPorId(session.company.id) : null;
    if (catalogada !== null) {
      return catalogada;
    }
    return {
      id: session.company.id,
      name: session.company.name,
      slug: session.company.id,
      segment: '',
      logoInitials: iniciais(session.company.name),
      logoTone: 1,
      logoUrl: null,
      feeBps: session.company.feeBps,
      billingModel: 'a_definir',
      status: 'ativa',
      providers: [],
      funnels: ['carrinho_abandonado'],
      contactName: session.user.name,
      contactEmail: session.user.email,
      createdAt: session.expiresAt,
    };
  }, [session]);

  const [ajustes, setAjustes] = useState<Ajustes>(() => (MOCK_FORCED ? lerAjustesDeEmpresas() : {}));
  // Modo real da equipe: `null` enquanto a lista nao chegou.
  const [remotas, setRemotas] = useState<readonly CompanySummary[] | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const remotasRef = useRef<readonly CompanySummary[] | null>(null);
  remotasRef.current = remotas;

  useEffect(() => {
    if (!listaRemota) {
      return undefined;
    }
    const controller = new AbortController();
    request<ListaDaEquipe>('/api/admin/companies', { signal: controller.signal })
      .then((resposta) => {
        if (!controller.signal.aborted && Array.isArray(resposta.companies)) {
          setRemotas(resposta.companies);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAviso('Não foi possível carregar a lista de empresas. O painel mostra a empresa da sua sessão até a lista voltar.');
        }
      });
    return () => {
      controller.abort();
    };
  }, [listaRemota, recarga]);

  const companies = useMemo<readonly CompanySummary[]>(() => {
    if (listaRemota) {
      return remotas !== null && remotas.length > 0 ? remotas : [daSessao];
    }
    return (MOCK_FORCED && isPlatformAdmin ? EMPRESAS : [daSessao]).map((c) => aplicarAjuste(c, ajustes));
  }, [listaRemota, remotas, isPlatformAdmin, daSessao, ajustes]);

  const [selectedId, setSelectedId] = useState<string>(() => {
    if (!isPlatformAdmin) {
      return daSessao.id;
    }
    const guardado = lerEscolha();
    if (listaRemota) {
      // A lista ainda nao chegou: guarda a escolha e confere depois (decisao 1).
      return guardado ?? daSessao.id;
    }
    return guardado !== null && companies.some((c) => c.id === guardado) ? guardado : (companies[0]?.id ?? EMPRESA_DEMO_ID);
  });

  const listaPronta = !listaRemota || remotas !== null;
  useEffect(() => {
    if (listaPronta && !companies.some((c) => c.id === selectedId)) {
      setSelectedId(companies.find((c) => c.id === daSessao.id)?.id ?? companies[0]?.id ?? daSessao.id);
    }
  }, [companies, selectedId, daSessao.id, listaPronta]);

  const selectCompany = useCallback(
    (id: string): void => {
      if (!isPlatformAdmin || !companies.some((c) => c.id === id)) {
        return;
      }
      guardarEscolha(id);
      setSelectedId(id);
    },
    [isPlatformAdmin, companies],
  );

  const updateCompany = useCallback(
    async (id: string, patch: CompanyPatch): Promise<boolean> => {
      if (!isPlatformAdmin) {
        return false;
      }
      if (!listaRemota) {
        setAjustes((atual) => {
          const proximo: Ajustes = { ...atual, [id]: { ...(atual[id] ?? {}), ...patch } };
          if (MOCK_FORCED) {
            guardarAjustes(proximo);
          }
          return proximo;
        });
        return true;
      }
      // Decisao 3: otimista, com desfazer.
      const antes = remotasRef.current;
      // O status so viaja quando MUDA na tela. O formulario de edicao manda o
      // status junto com nome e segmento; sem este corte, renomear uma empresa
      // `churned` (que a tela mostra como `pausada`) a regravaria `suspended`
      // no banco -- uma escrita que ninguem pediu (revisao de 25/09/2026).
      const atual = antes?.find((c) => c.id === id);
      const corpo = corpoDoPatch(
        patch.status !== undefined && atual !== undefined && atual.status === patch.status ? { ...patch, status: undefined } : patch,
      );
      if (Object.keys(corpo).length === 0) {
        return true;
      }
      setRemotas((lista) => (lista === null ? lista : lista.map((c) => (c.id === id ? { ...c, ...patch } : c))));
      try {
        const resposta = await request<{ readonly company: CompanySummary }>(`/api/admin/companies/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: corpo,
        });
        setRemotas((lista) => (lista === null ? lista : lista.map((c) => (c.id === id ? resposta.company : c))));
        setAviso(null);
        return true;
      } catch (error) {
        setRemotas(antes);
        setAviso(mensagemDeFalha(error));
        return false;
      }
    },
    [isPlatformAdmin, listaRemota],
  );

  const reloadCompanies = useCallback((): void => {
    setRecarga((n) => n + 1);
  }, []);

  const company = companies.find((c) => c.id === selectedId) ?? (MOCK_FORCED ? aplicarAjuste(daSessao, ajustes) : daSessao);

  // Decisao 2: o foco vale ANTES de qualquer filho buscar dado. So a equipe,
  // so em modo real, so com a lista carregada e so para outra empresa (a da
  // propria sessao dispensa o cabecalho).
  // Sem efeito de limpeza de proposito: no StrictMode o desmonte simulado
  // zeraria o foco sem novo render. Um valor velho apos o logout e inofensivo
  // (rotas de login ignoram o cabecalho) e o proximo render deste provedor o
  // sobrescreve antes de qualquer busca.
  definirEmpresaEmFoco(listaRemota && remotas !== null && company.id !== session.company.id ? company.id : null);

  const value = useMemo<CompanyContextValue>(
    () => ({ company, companies, isPlatformAdmin, selectCompany, updateCompany, reloadCompanies }),
    [company, companies, isPlatformAdmin, selectCompany, updateCompany, reloadCompanies],
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
      {aviso === null ? null : (
        <div
          role="alert"
          className="fixed bottom-4 right-4 z-50 flex max-w-sm items-start gap-3 rounded-xl border border-status-critical/40 bg-surface px-4 py-3 text-sm text-ink shadow-float"
        >
          <p className="min-w-0 flex-1">{aviso}</p>
          <button type="button" onClick={() => setAviso(null)} className="shrink-0 text-xs font-medium text-ink-muted underline underline-offset-4 hover:text-ink">
            Fechar
          </button>
        </div>
      )}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextValue {
  const value = useContext(CompanyContext);
  if (value === null) {
    throw new Error('useCompany fora de CompanyProvider');
  }
  return value;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 0);
  const primeira = partes[0]?.charAt(0) ?? 'C';
  const segunda = partes.length > 1 ? partes[partes.length - 1]?.charAt(0) ?? '' : partes[0]?.charAt(1) ?? '';
  return `${primeira}${segunda}`.toUpperCase();
}
